/**
 * Browser voice-message capture.
 *
 * Wraps MediaRecorder so callers don't deal with the chunk-by-chunk
 * dataavailable / stop-event dance. A successful recording resolves
 * with the assembled Blob + duration; the caller handles encryption
 * (via AttachmentService) and message dispatch.
 *
 * The first MIME type the browser supports from a small preference
 * list is used. Safari historically only supports `audio/mp4`; every
 * Chromium/Firefox build supports `audio/webm`.
 */

const PREFERRED_MIMES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
] as const;

export interface VoiceRecording {
  readonly bytes: Uint8Array;
  readonly mime: string;
  /** Duration in milliseconds. */
  readonly durationMs: number;
}

function pickMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  for (const candidate of PREFERRED_MIMES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return undefined;
}

export class VoiceRecorder {
  #recorder: MediaRecorder | undefined;
  #stream: MediaStream | undefined;
  #chunks: BlobPart[] = [];
  #startedAt = 0;
  #stopPromise: Promise<VoiceRecording> | undefined;

  /**
   * Request mic access and start recording. Throws if the browser
   * doesn't support MediaRecorder, the user denies the permission,
   * or no compatible MIME type is available.
   */
  async start(): Promise<void> {
    if (this.#recorder) throw new Error('already recording');
    const mime = pickMime();
    if (!mime) throw new Error('audio recording not supported on this browser');
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('microphone access not available');
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream, { mimeType: mime });
    this.#stream = stream;
    this.#recorder = recorder;
    this.#chunks = [];
    this.#startedAt = Date.now();
    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) this.#chunks.push(event.data);
    });
    this.#stopPromise = new Promise<VoiceRecording>((resolve, reject) => {
      recorder.addEventListener('stop', () => {
        this.#stream?.getTracks().forEach((t) => {
          t.stop();
        });
        const blob = new Blob(this.#chunks, { type: mime });
        const durationMs = Date.now() - this.#startedAt;
        blob
          .arrayBuffer()
          .then((buf) => {
            resolve({ bytes: new Uint8Array(buf), mime, durationMs });
          })
          .catch(reject);
        this.#recorder = undefined;
        this.#stream = undefined;
      });
      recorder.addEventListener('error', () => {
        reject(new Error('recorder errored'));
      });
    });
    recorder.start();
  }

  /** Stop the recording and resolve with the assembled blob. */
  async stop(): Promise<VoiceRecording> {
    if (!this.#recorder || !this.#stopPromise) throw new Error('not recording');
    if (this.#recorder.state !== 'inactive') this.#recorder.stop();
    return this.#stopPromise;
  }

  /** Cancel and discard the recording. */
  cancel(): void {
    try {
      if (this.#recorder && this.#recorder.state !== 'inactive') this.#recorder.stop();
    } catch {
      // already stopping
    }
    this.#stream?.getTracks().forEach((t) => {
      t.stop();
    });
    this.#recorder = undefined;
    this.#stream = undefined;
    this.#chunks = [];
    this.#stopPromise = undefined;
  }

  isRecording(): boolean {
    return this.#recorder !== undefined;
  }
}

export function isVoiceRecordingAvailable(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    pickMime() !== undefined
  );
}
